import couImg from '@/assets/images/tms/cou.png?url';
import epauleImg from '@/assets/images/tms/epaule.png?url';
import dosImg from '@/assets/images/tms/dos.png?url';
import coudeImg from '@/assets/images/tms/coude.png?url';
import poignetImg from '@/assets/images/tms/poignet.png?url';
import postureImg from '@/assets/images/tms/posture.png?url';
import etirementsGenerauxImg from '@/assets/images/tms/etirements-generaux.png?url';

export const BODY_PARTS_ORDER = ['epaule', 'poignet', 'cou', 'dos', 'coude', 'autre'] as const;

export type BodyPartKey = (typeof BODY_PARTS_ORDER)[number];

type Question = { label: string; key: string; type: 'oui_non' | 'pain_level' };

type Syndrome = { label: string; field: string; question: string };

type BodyPartConfig = {
  label: string;
  riskField: string;
  questions: Question[];
  syndromes: Syndrome[];
  recommendation: { title: string; content: string };
};

// Intro commune aux 5 zones spécifiques du PDF
const EXERCISES_INTRO = `<b>Exercices de mobilité et étirements</b>
Il est important de prendre des pauses régulièrement et de rester en mouvement. Pour ce faire, vous pouvez réaliser les exercices d’étirements suivants.
Les positions d’étirement proposées doivent être maintenues pendant 30 secondes.

En cas de douleurs, stoppez immédiatement les exercices.`;

// Pages « Recommandations générales » du PDF, rattachées à la zone « Autre »
const GENERAL_RECOMMENDATION = `
Afin de prévenir au maximum les risques de troubles musculo-squelettiques, nous vous conseillons de suivre les recommandations suivantes :

<b>Posture</b>

<img src="${postureImg}" alt="Position recommandée pour le travail devant un écran" />

Lors du travail devant un écran, il est recommandé de respecter la position suivante :

- Les pieds reposent à plat sur le sol ou sur un repose-pied
- L’angle du coude est droit ou légèrement plus grand
- Les avant-bras sont proches du corps
- La main est dans le prolongement de l'avant-bras
- Le dos est droit ou légèrement en arrière, soutenu par le dossier.

Attention, cette position « idéale » est difficile à tenir sur la durée, lorsque vous en ressentez le besoin, n’hésitez pas à changer de position. Le plus important est de faire des pauses actives régulièrement.

<b>Se mettre en mouvement</b>

- Aménager une pause d’au moins 5 minutes toutes les heures si la tâche sur l'écran est intensive ou bien d'un quart d'heure toutes les 2 heures si la tâche l’est moins. Durant ces pauses, il est conseillé de quitter son poste de travail et de bouger pour « rompre » la posture statique prolongée liée au travail sur écran.
- Le travail sur écran peut également engendrer des fatigues oculaires (picotements, brûlures…), il est conseillé de cligner volontairement des yeux et faire des pauses régulières sans regarder les écrans (toutes les heures).
- Faire des pauses actives : passage en position debout, étirements musculaires, gymnastiques de pause ou encore activités sportives

<b>Exemples d’étirements</b>

<img src="${etirementsGenerauxImg}" alt="Exemples d’étirements" />

<b>Exercice de relaxation</b>

Le stress est un facteur favorisant les TMS. Si vous vous sentez stressé, essayez l’exercice de cohérence cardiaque suivant :

Exercice 365
- 3 fois par jour, 6 respirations par minute, pendant 5 minutes.
- Il suffit d’inspirer pendant 5 secondes et expirer sur 5 secondes.

<b>En cas de douleurs</b>

- Des automassages au niveau de la zone douloureuse peuvent aider à soulager les douleurs.
- En cas de douleur aiguë, appliquez une poche réfrigérante sur la zone douloureuse, afin de diminuer la douleur (attention aux brûlures de la peau par le froid).
- Appliquez une bouillotte chaude dans le cas de douleurs musculaires. La chaleur permettra le relâchement.
`;

function specificRecommendation(
  syndromes: string,
  img: string,
  alt: string,
  dailyTips: string[],
): string {
  return `
<b>${syndromes}</b>

${EXERCISES_INTRO}

<img src="${img}" alt="${alt}" />

<b>Au quotidien</b>
${dailyTips.map((tip) => `- ${tip}`).join('\n')}
`;
}

export const tmsBodyPartsConfig: Record<BodyPartKey, BodyPartConfig> = {
  cou: {
    label: 'Cou',
    riskField: 'neck_pain_risk',
    questions: [
      {
        label: 'Problèmes au cou au cours des 12 derniers mois',
        key: 'neck_pain_last_twelve_months',
        type: 'oui_non',
      },
      { label: 'Au cours des 7 derniers jours', key: 'neck_pain_last_seven_days', type: 'oui_non' },
      { label: 'Niveau de douleur', key: 'neck_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [
      {
        label: 'Cervicalgie',
        field: 'form_of_neck_pain',
        question:
          'Le participant ressent-il au moins une douleur intermittente ou une raideur dans le cou ?',
      },
    ],
    recommendation: {
      title: 'Zone du cou',
      content: specificRecommendation('Cervicalgie', couImg, 'Exercices d’étirement du cou', [
        "Continuez vos activités en les adaptant. Veillez à vos positions et bonnes postures, en choisissant celles qui vous créent le moins de douleurs cervicales en évitant les attitudes figées trop longtemps et les mouvements brusques. Il est essentiel de maintenir une activité physique, même minime. La reprise d'une activité physique adaptée est la meilleure garantie pour une guérison rapide.",
        "Vérifiez que votre literie (matelas, oreiller) s'adapte bien à votre morphologie et qu'elle est de bonne qualité. La position à plat ventre et l'utilisation d'un traversin la nuit sont déconseillées lorsqu'on a mal au cou.",
        'En cas de stress, pensez à essayer des exercices de relaxation.',
      ]),
    },
  },
  dos: {
    label: 'Dos',
    riskField: 'back_pain_risk',
    questions: [
      {
        label: 'Problèmes au dos au cours des 12 derniers mois',
        key: 'back_pain_last_twelve_months',
        type: 'oui_non',
      },
      { label: 'Au cours des 7 derniers jours', key: 'back_pain_last_seven_days', type: 'oui_non' },
      { label: 'Niveau de douleur', key: 'back_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [
      {
        label: 'Lombalgie',
        field: 'form_of_low_back_pain',
        question:
          'Le participant ressent-il au moins une douleur intermittente ou une raideur dans la région des lombaires ?',
      },
    ],
    recommendation: {
      title: 'Zone du dos',
      content: specificRecommendation('Lombalgie', dosImg, 'Exercices d’étirement du dos', [
        "Continuez vos activités en les adaptant. Veillez à vos positions et bonnes postures, en choisissant celles qui vous créent le moins de douleurs en évitant les attitudes figées trop longtemps et les mouvements brusques. Il est essentiel de maintenir une activité physique, même minime. La reprise d'une activité physique adaptée est la meilleure garantie pour une guérison rapide.",
        "Vérifiez que votre literie (matelas, oreiller) s'adapte bien à votre morphologie et qu'elle est de bonne qualité. Pour dormir en position de chien de fusil, un traversin la nuit entre les jambes permet d'équilibrer le bassin.",
        'Détendez-vous. Pensez à la relaxation surtout si, dans votre cas, le stress est un facteur aggravant.',
      ]),
    },
  },
  epaule: {
    label: 'Épaule',
    riskField: 'shoulder_pain_risk',
    questions: [
      {
        label: "Problèmes à l'épaule au cours des 12 derniers mois",
        key: 'shoulder_pain_last_twelve_months',
        type: 'oui_non',
      },
      {
        label: 'Au cours des 7 derniers jours',
        key: 'shoulder_pain_last_seven_days',
        type: 'oui_non',
      },
      { label: 'Niveau de douleur', key: 'shoulder_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [
      {
        label: 'Syndrome de la coiffe des rotateurs',
        field: 'form_of_rotator_cuff',
        question:
          "Le participant ressent-il au moins une douleur intermittente de l'épaule, sans paresthésie ? Cette douleur est-elle accentuée par l'abduction active de l'épaule (pour se gratter le haut du dos par exemple) ?",
      },
    ],
    recommendation: {
      title: 'Zone des épaules',
      content: specificRecommendation(
        'Syndrome de la coiffe des rotateurs',
        epauleImg,
        'Exercices d’étirement des épaules',
        [
          "Evitez les mouvements du bras répétitifs ou prolongés au-dessus de l'horizontal, les mouvements brusques, les postures et situations qui augmentent les douleurs",
          'Evitez de porter de lourdes charges',
          "Pour ne pas avoir mal la nuit, ne dormez pas sur l'épaule douloureuse. Dormez sur le dos ou sur le côté opposé et positionnez confortablement le membre concerné par les douleurs en le posant, si nécessaire, sur un oreiller placé sous l'avant-bras",
          "Pour vous habiller, enfilez vos vêtements en commençant par l'épaule douloureuse. Faites-vous aider pour toutes les fermetures situées dans le dos : agrafe de soutien-gorge par exemple",
          'Supprimez les activités sportives nocives pour votre épaule (sports de lancer ou à risque de chute).',
        ],
      ),
    },
  },
  coude: {
    label: 'Coude',
    riskField: 'elbow_pain_risk',
    questions: [
      {
        label: 'Problèmes au coude au cours des 12 derniers mois',
        key: 'elbow_pain_last_twelve_months',
        type: 'oui_non',
      },
      {
        label: 'Au cours des 7 derniers jours',
        key: 'elbow_pain_last_seven_days',
        type: 'oui_non',
      },
      { label: 'Niveau de douleur', key: 'elbow_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [
      {
        label: 'Épicondylite latérale ou médiale',
        field: 'form_of_lateral_epicondylitis',
        question:
          "Le participant ressent-il au moins une douleur intermittente, liée à l'activité manuelle, directement localisée dans la zone de l'épicondyle latérale ou médiale (épitrochlée) ?",
      },
    ],
    recommendation: {
      title: 'Zone du coude',
      content: specificRecommendation(
        'Épicondylite latérale ou médiale',
        coudeImg,
        'Exercices d’étirement du coude',
        [
          'Effectuez des mouvements souples plutôt que saccadés',
          'Avant tout travail de force ou effort, échauffez-vous et buvez de l’eau',
          'Pendant l’effort, essayez de garder le coude fléchi plutôt que tendu',
          'Lorsque vous portez ou soulevez un objet, placez de préférence votre main paume vers le haut',
          'Evitez tout serrage intensif (ex. : vissage en force)',
          'Essayez d’alterner d’éventuelles tâches répétitives avec d’autres types de travaux, ou faites des pauses',
          'Repérez les mouvements à l’origine des douleurs, puis si possible, arrêtez les activités en cause',
          'Appliquez une poche réfrigérante sur la zone douloureuse du coude, afin de diminuer la douleur (attention aux brûlures de la peau par le froid).',
        ],
      ),
    },
  },
  poignet: {
    label: 'Poignet',
    riskField: 'wrist_pain_risk',
    questions: [
      {
        label: 'Problèmes au poignet/main au cours des 12 derniers mois',
        key: 'wrist_pain_last_twelve_months',
        type: 'oui_non',
      },
      {
        label: 'Au cours des 7 derniers jours',
        key: 'wrist_pain_last_seven_days',
        type: 'oui_non',
      },
      { label: 'Niveau de douleur', key: 'wrist_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [
      {
        label: 'Syndrome du canal carpien',
        field: 'form_of_carpal_tunnel',
        question:
          'Le participant ressent-il des paresthésies ou une douleur intermittente dans au moins deux des trois premiers doigts, pouvant aussi être présentes la nuit ? La douleur peut siéger dans la paume de la main ou irradier vers le poignet.',
      },
      {
        label: 'Syndrome du canal de Guyon',
        field: 'form_of_guyon_canal',
        question:
          "Le participant ressent-il des paresthésies intermittentes dans le territoire du nerf ulnaire au niveau du bord ulnaire de la main, ou une douleur dans la région ulnaire et palmaire de la main, pouvant irradier dans l'avant-bras ?",
      },
      {
        label: 'Ténosynovite de De Quervain',
        field: 'form_of_de_quervain_tenosynovitis',
        question:
          'Le participant ressent-il au moins une douleur intermittente ou une sensibilité du bord radial du poignet pouvant irradier vers l’avant-bras ou le pouce ?',
      },
      {
        label: 'Tendinite des extenseurs',
        field: 'form_of_extensor_tendonitis',
        question:
          "Le participant ressent-il au moins une douleur intermittente dans la région dorsale du poignet ou de l'avant-bras ?",
      },
      {
        label: 'Tendinite des fléchisseurs',
        field: 'form_of_flexor_tendonitis',
        question:
          "Le participant ressent-il au moins une douleur intermittente dans la région palmaire du poignet ou de l'avant-bras ?",
      },
    ],
    recommendation: {
      title: 'Zone poignet / main',
      content: specificRecommendation(
        'Syndrome du canal carpien, du canal de Guyon, ténosynovite de De Quervain, tendinite des extenseurs et tendinite des fléchisseurs',
        poignetImg,
        'Exercices d’étirement et de renforcement du poignet et de la main',
        [
          'Lorsque vous utilisez vos mains, positionnez-les dans le prolongement de vos avant-bras ou en légère extension',
          'Limitez la répétitivité des mouvements de votre poignet',
          "Lorsque vous utilisez une machine responsable de vibrations (perceuse par exemple), utilisez des gants afin d'en diminuer les conséquences",
          'Appliquez une poche réfrigérante sur la zone douloureuse, afin de diminuer la douleur (attention aux brûlures de la peau par le froid)',
        ],
      ),
    },
  },
  autre: {
    label: 'Autre',
    riskField: 'other_pain_risk',
    questions: [
      {
        label: 'Problèmes à une autre zone au cours des 12 derniers mois',
        key: 'other_pain_area',
        type: 'oui_non',
      },
      {
        label: 'Au cours des 7 derniers jours',
        key: 'other_pain_last_seven_days',
        type: 'oui_non',
      },
      { label: 'Niveau de douleur', key: 'other_pain_intensity', type: 'pain_level' },
    ],
    syndromes: [],
    recommendation: {
      title: 'Recommandations générales',
      content: GENERAL_RECOMMENDATION,
    },
  },
};

export const tmsQuestionsConfig = Object.fromEntries(
  BODY_PARTS_ORDER.map((key) => {
    const config = tmsBodyPartsConfig[key];
    return [
      key,
      {
        title: config.label,
        description: undefined,
        items: config.questions.map((q) => ({ label: q.label, key: q.key, type: q.type })),
      },
    ];
  }),
);

type RiskInfo = {
  label: string;
  class: string;
  tone: 'error' | 'warning' | 'success' | 'secondary';
};

export function getRiskInfo(risk: string | undefined | null): RiskInfo {
  switch (risk) {
    case 'HIGH':
      return { label: 'Élevé', class: 'text-error-solid', tone: 'error' };
    case 'MODERATE':
      return { label: 'Modéré', class: 'text-warning-primary', tone: 'warning' };
    case 'LOW':
      return { label: 'Faible', class: 'text-success-primary', tone: 'success' };
    case 'UNBEARABLE':
      return { label: 'Insupportable', class: 'text-error-solid', tone: 'error' };
    default:
      return { label: 'Non évalué', class: 'text-tertiary', tone: 'secondary' };
  }
}
